import { useEffect, useRef } from 'react';
import type { FC, LegacyRef } from 'react';

import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { CloseFill as CloseFillIcon } from '@vgno/icons';

import { getPortalElement } from '../../utils/getPortalElement';
import { useScrollLock } from '../utils/useScrollLock';

import * as styles from './Overlay.module.scss';

type OverlayType =
  | 'overlay/popup'
  | 'overlay/drawer'
  | 'overlay/topDrawer'
  | 'overlay/rightDrawer';

export interface OverlayProps {
  type?: OverlayType;
  isOpen: boolean;
  isTransparent?: boolean;
  className?: string;
  contentClassName?: string;
  onCloseButtonClick?: () => void;
  onBackdropClick?: () => void;
  forceRender?: boolean;
  scrollContainerRef?: LegacyRef<HTMLDivElement>;
  disableScrollLock?: boolean;
}

export const Overlay: FC<OverlayProps> = ({
  children,
  type = 'overlay/popup',
  isOpen,
  isTransparent,
  className,
  contentClassName,
  onCloseButtonClick,
  onBackdropClick,
  forceRender = false,
  scrollContainerRef,
  disableScrollLock,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useScrollLock(!disableScrollLock && isOpen);

  // when a drawer is open, this adds listeners for closing the drawer when clicked outside of it
  useEffect(() => {
    function handleCloseOverlay(event: DocumentEventMap['click']) {
      const container = onBackdropClick
        ? contentRef.current
        : containerRef.current;

      if (
        container &&
        container !== event.target &&
        !container.contains(event.target as Node) &&
        isOpen
      ) {
        event.preventDefault(); // avoid navigation when clicking on an item below

        if (onBackdropClick) {
          // call the backdrop click handler with a small delay
          // to avoid interference with close button that's outside the container
          setTimeout(onBackdropClick, 50);
        } else if (onCloseButtonClick) {
          onCloseButtonClick();
        }
      }
    }

    if (isOpen && onBackdropClick instanceof Function) {
      document.addEventListener('click', handleCloseOverlay, true);

      return () => {
        document.removeEventListener('click', handleCloseOverlay, true);
      };
    }
  }, [isOpen, onBackdropClick, onCloseButtonClick, type]);

  const content = (
    <div
      className={classNames(
        styles.backdrop,
        {
          [styles.isPopup]: type === 'overlay/popup',
          [styles.isDrawer]: type === 'overlay/drawer',
          [styles.isTopDrawer]: type === 'overlay/topDrawer',
          [styles.isRightDrawer]: type === 'overlay/rightDrawer',
          [styles.isForceRendered]: forceRender,
          [styles.isOpen]: forceRender && isOpen,
          [styles.isTransparent]: isTransparent,
        },
        className
      )}
      ref={containerRef}
    >
      <div
        className={classNames(styles.content, contentClassName)}
        ref={scrollContainerRef}
      >
        {onBackdropClick ? (
          <div className={styles.contentInner} ref={contentRef}>
            {children}
          </div>
        ) : (
          <>{children}</>
        )}
      </div>
      {onCloseButtonClick && (
        <button
          type="button"
          className={styles.closeButton}
          onClick={onCloseButtonClick}
          aria-label={t('Close button')}
        >
          <CloseFillIcon />
        </button>
      )}
    </div>
  );

  return isOpen || forceRender
    ? createPortal(content, getPortalElement())
    : null;
};
